// allhomes detail-page parser. allhomes (Domain Group) property pages embed
// the page data as a script assignment:
//   window['__domain_group/APP_PROPS'] = {…}; window['__domain_group/APP_PAGE'] = …
// body.property is THIS property: address (line1, division.name = suburb,
// state.abbreviation, postcode, coordinates), features (propertyType enum,
// bedrooms, bathrooms.total, parking.total) and history, its sale events.
//
// The pages we scrape are property profiles ("Property & Past Sales"), not
// sold-listing pages: listing.type is OFF_MARKET and the sale is the newest
// event in history whose transfer label starts with "Sold" (e.g. "Sold at
// auction"). A property with no such event has never sold according to the
// page; it yields no sale even when it shows rentals ("$2,000 per week"), and
// skipSnapshotReason: "never_sold" so the workers save no snapshot for it
// (George, 2026-10-12; snapshot-skip.ts in @repo/adapter-core).
//
// Never read address.soldComparables / leasedComparables / nearbySales,
// rentalHistory or __APOLLO_STATE__ (price estimates): they hold other
// properties' sales, rents and estimates.

import {
  areaValue,
  count,
  formatAddress,
  htmlText,
  isRecord,
  parseListingDate,
  propertyTypeLabel,
  readJsonObjectAt,
  stateCode,
  text,
} from "../shared/listing-html.ts";

const APP_PROPS_MARKER = "window['__domain_group/APP_PROPS']";
// Two records of one sale (the listing's and the government transfer's) sit a
// few days apart; only then may the transfer's price stand in for a missing one.
const SAME_SALE_DAYS = 7;

interface SaleEvent {
  date: string;
  price: number | null;
  blockSize: number | null;
  agency: string | null;
}

function readProperty(html: string, url: string): Record<string, unknown> | null {
  const marker = html.indexOf(APP_PROPS_MARKER);
  if (marker < 0) return null;
  const props = readJsonObjectAt(html, html.indexOf("{", marker));
  const body = props && isRecord(props.body) ? props.body : null;
  const property = body && isRecord(body.property) ? body.property : null;
  if (!property || !isRecord(property.address)) return null;
  // The address slug is the page path; a different slug is a different property.
  const path = url
    .replace(/[?#].*$/, "")
    .replace(/\/+$/, "")
    .split("/")
    .pop()
    ?.toLowerCase();
  const slug = text(property.address.slug)?.toLowerCase();
  if (path && slug && path !== slug) return null;
  return property;
}

function saleEvents(history: unknown): SaleEvent[] {
  if (!Array.isArray(history)) return [];
  const events: SaleEvent[] = [];
  for (const event of history) {
    if (!isRecord(event) || !isRecord(event.transfer)) continue;
    const transfer = event.transfer;
    if (!/^sold\b/i.test(text(transfer.label) ?? "")) continue;
    const date = parseListingDate(text(transfer.contractDate) ?? text(event.date));
    if (!date) continue;
    const price =
      typeof transfer.price === "number" && transfer.price >= 10_000 ? transfer.price : null;
    const blockSize =
      typeof transfer.blockSize === "number" && transfer.blockSize > 0 ? transfer.blockSize : null;
    const agents = Array.isArray(event.agents) ? event.agents : [];
    const agency =
      agents
        .map((agent) =>
          isRecord(agent) && isRecord(agent.agency) ? text(agent.agency.name) : null,
        )
        .find(Boolean) ?? null;
    events.push({ date, price, blockSize, agency });
  }
  return events.sort((a, b) => b.date.localeCompare(a.date));
}

function daysBetween(a: string, b: string): number {
  return Math.abs(Date.parse(a) - Date.parse(b)) / 86_400_000;
}

export function parseAllhomesDetailHtml(html: string, url: string): Record<string, unknown> {
  const property = readProperty(html, url);
  if (!property) return {};
  const address = property.address as Record<string, unknown>;
  const street = text(address.line1);
  if (!street) return {};
  const suburb = isRecord(address.division) ? text(address.division.name) : null;
  const state = isRecord(address.state) ? stateCode(text(address.state.abbreviation)) : null;
  const postcode = text(address.postcode);

  const hit: Record<string, unknown> = {
    address: formatAddress(street, suburb, state, postcode),
    suburb,
    state,
    postcode,
  };

  const events = saleEvents(property.history);
  const sale = events[0] ?? null;
  const partner =
    sale && sale.price === null
      ? events.find(
          (e) => e !== sale && e.price !== null && daysBetween(e.date, sale.date) <= SAME_SALE_DAYS,
        )
      : null;
  hit.listingType = sale ? "sold" : null;
  hit.saleDate = sale?.date ?? null;
  hit.salePrice = sale?.price ?? partner?.price ?? null;
  hit.price = hit.salePrice;
  // The page's own "Last sold <date> $<price>" line, for auditing.
  const lastSold = html.match(/Last sold[\s\S]{0,120}?<\/span><span[^>]*>([\s\S]{0,80}?)<\/span>/i);
  hit.priceDisplay = sale && lastSold ? text(htmlText(lastSold[1]).replace(/\$\s+/, "$")) : null;
  hit.agencyName = sale?.agency ?? null;
  if (!sale) hit.skipSnapshotReason = "never_sold";

  const features = isRecord(property.features) ? property.features : {};
  hit.bedrooms = count(features.bedrooms);
  hit.bathrooms = isRecord(features.bathrooms) ? count(features.bathrooms.total) : null;
  hit.parking = isRecord(features.parking) ? count(features.parking.total) : null;
  hit.propertyType = propertyTypeLabel(text(features.propertyType));

  // Block size: the cadastral parcel's, else the newest government transfer
  // record that states one (shown on the page as "Block size: 632m²").
  const parcel = isRecord(address.cadastralParcel) ? address.cadastralParcel.blockSize : null;
  hit.landSize =
    areaValue(parcel) ?? areaValue(events.find((e) => e.blockSize !== null)?.blockSize ?? null);
  hit.landSizeUnit = hit.landSize ? "sqm" : null;
  const building = areaValue(features.buildingSize) ?? areaValue(features.floorArea);
  if (building) {
    hit.propertySize = building;
    hit.propertySizeUnit = "sqm";
  }

  const coordinates = isRecord(address.coordinates) ? address.coordinates : null;
  const lat = coordinates?.lat;
  const lng = coordinates?.lon ?? coordinates?.lng;
  if (typeof lat === "number" && typeof lng === "number" && lat !== 0 && lng !== 0) {
    hit.latitude = lat;
    hit.longitude = lng;
  }

  return hit;
}
